import { GatsbyImage, getImage } from "gatsby-plugin-image"
import cc from "classcat"
import FormTrainings from "components/organisms/schools/FormTrainings"
import { RichText } from "prismic-reactjs"
import SectionContainer from "components/molecules/common/SectionContainer"
import { isWebinarNowLive } from "utils/date"
import { OutboundLink } from "components/atoms/OutboundLink"
import LiveTag from "components/atoms/common/LiveTag"
import useTranslations from "hooks/useTranslations"
import { EventInfo } from "./EventInfo"

const Speaker = ({ name, photo, alt, description, id, subject }) => {
  return (
    <div className="grid-cols-7 space-y-4 lg:grid lg:space-x-6 lg:space-y-0">
      <div className="col-span-7 flex grow space-x-4" id={id}>
        <GatsbyImage
          image={getImage(photo)}
          className="z-[1] h-16 w-16 shrink-0 rounded-full lg:h-20 lg:w-20 "
          alt={alt}
        />
        <div className="flex grow flex-col space-y-1">
          <p className="m-0 mb-2 font-display text-lg leading-5 text-white">
            {name}
          </p>
          <p className="prismic-rich-text m-0 grow font-display text-sm text-kkm-darkgreen">
            <RichText render={description} />
          </p>
        </div>
      </div>
      {subject ? (
        <div className="prismic-rich-text col-span-4 m-0 flex grow items-center font-display text-xl text-white">
          <RichText render={subject} />
        </div>
      ) : null}
    </div>
  )
}

export const Speakers = ({ speakers }) => {
  return (
    <div className="text-white">
      <h2 className="m-0 mb-4 whitespace-pre-line text-3xl font-extrabold leading-none tracking-tight sm:text-4xl lg:text-4xl">
        Puhujat
      </h2>
      <div className="flex flex-col gap-6">
        {speakers.map((speaker) => (
          <Speaker {...speaker} key={speaker.name} />
        ))}
      </div>
    </div>
  )
}

export const Content = ({ children, className = "" }) => {
  return (
    <div className={cc(["prismic-rich-text space-y-4 text-xl", className])}>
      <RichText render={children} />
    </div>
  )
}

export const LiveLink = ({ url }) => {
  const { trainingLiveLink1, trainingLiveLink2 } = useTranslations()
  return (
    <div className="relative mx-auto flex flex-col rounded-lg bg-zinc-700 px-4 pb-7 pt-6 sm:px-6">
      <LiveTag />
      <div className="prismic-rich-text space-y-4 text-xl text-white">
        <div className="">
          {trainingLiveLink1}
          <OutboundLink
            href={url}
            target="_blank"
            className="animate-pulse cursor-pointer"
          >
            {trainingLiveLink2}
          </OutboundLink>
        </div>
      </div>
    </div>
  )
}

type InfoProps = {
  webinars?: Webinar[]
  webinar: Webinar
  setActiveWebinar?: (webinar: Webinar) => void
  segment?: "schools" | "professionals" | ""
  disabled?: boolean
  showLiveLink?: boolean
}

export default function Info({
  webinar,
  webinars,
  setActiveWebinar,
  segment,
  disabled,
  showLiveLink = true,
}: InfoProps) {
  const visibleLink =
    isWebinarNowLive(webinar?.time, webinar?.end) && showLiveLink
  return (
    <SectionContainer className="bg-zinc-800" id="info" width="wide">
      <div
        className={cc([
          " gap-x-16 space-y-16 lg:grid lg:space-y-0",
          disabled ? "max-w-4xl grid-cols-1" : "grid-cols-2",
        ])}
      >
        <EventInfo webinar={webinar} visibleLink={visibleLink} />
        {!disabled ? (
          <FormTrainings
            webinar={webinar}
            webinars={webinars}
            setActiveWebinar={setActiveWebinar}
            segment={segment}
          />
        ) : null}
      </div>
    </SectionContainer>
  )
}
